import { styled } from "goober";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { truncateMiddle } from "../lib/stepUrl";
import { writeToClipboard } from "../lib/writeToClipboard";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { AnimatedCopyIcon } from "./AnimatedCopyIcon";
import { Icon } from "./Icon";
import { PressButton } from "./PressButton";
import { Text } from "./Text";
import {
  type Align,
  getLastTooltipCloseTime,
  getPositionStyles,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  type Vertical,
  WARM_PERIOD,
} from "./Tooltip";

const HIDE_DELAY = 300;

let openLinkTooltipCount = 0;
const linkTooltipCloseListeners = new Set<() => void>();

export function isLinkTooltipOpen() {
  return openLinkTooltipCount > 0;
}

export function onLinkTooltipsClosed(listener: () => void) {
  linkTooltipCloseListeners.add(listener);
  return () => linkTooltipCloseListeners.delete(listener);
}

function registerOpenLinkTooltip() {
  openLinkTooltipCount += 1;
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    openLinkTooltipCount -= 1;
    if (openLinkTooltipCount > 0) return;
    const listeners = [...linkTooltipCloseListeners];
    linkTooltipCloseListeners.clear();
    for (const listener of listeners) listener();
  };
}

const Container = styled("div")`
  display: inline-flex;
`;

const Trigger = styled("div")`
  display: inline-flex;
  border-radius: 8px;
  cursor: pointer;
`;

const PopoverContent = styled(AnchoredPopover)`
  background: var(--tooltip-bg);
  color: var(--tooltip-fg);
  box-shadow: var(--elevation-low);
  padding: 2px 2px 2px 8px;
  z-index: ${zIndexes.tooltip};
  max-width: 300px;
  width: max-content;
  pointer-events: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
`;

const ButtonGroup = styled("div")`
  position: relative;
  display: flex;
  align-items: center;
`;

const ButtonHighlight = styled("div")`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: var(--tooltip-highlight);
  transition:
    transform 150ms ease,
    opacity 100ms ease;
  pointer-events: none;
`;

const ActionButton = styled(PressButton)`
  position: relative;
  background: transparent;
  border: 0;
  padding: 4px;
  border-radius: 9999px;
  cursor: pointer;
  color: var(--tooltip-fg-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 150ms ease;
  &:hover {
    color: var(--tooltip-fg);
  }
`;

const UrlText = styled(Text)`
  white-space: nowrap;
  color: var(--tooltip-fg-muted);
`;

type Props = {
  url: string;
  openUrl?: string;
  children: React.ReactNode;
};

export function LinkTooltip({ url, openUrl, children }: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<number | null>(null);
  const [align, setAlign] = useState<Align>("center");
  const [vertical, setVertical] = useState<Vertical>("above");
  const triggerRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hide = () => {
    setLastTooltipCloseTime(Date.now());
    setIsVisible(false);
  };

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const startHideTimer = () => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(hide, HIDE_DELAY);
  };

  const showTooltip = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (rect.left < 100) {
        setAlign("left");
      } else if (viewportWidth - rect.right < 100) {
        setAlign("right");
      } else {
        setAlign("center");
      }

      if (rect.top < 60) {
        setVertical("below");
      } else {
        setVertical("above");
      }
    }
    setIsVisible(true);
  };

  const handleTriggerMouseEnter = () => {
    clearHideTimer();
    const isWarm = Date.now() - getLastTooltipCloseTime() < WARM_PERIOD;
    if (isWarm) {
      showTooltip();
    } else {
      delayTimerRef.current = setTimeout(showTooltip, TOOLTIP_DELAY);
    }
  };

  const handleTriggerMouseLeave = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (isVisible) {
      startHideTimer();
    }
  };

  const handlePopoverMouseEnter = () => {
    clearHideTimer();
  };

  const handlePopoverMouseLeave = () => {
    startHideTimer();
  };

  const handleActionMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCopy = () => {
    void writeToClipboard(url);
    setCopied(true);
    if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 1200);
  };

  const handleOpen = () => {
    window.open(openUrl ?? url, "_blank");
  };

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isVisible) return;
    return registerOpenLinkTooltip();
  }, [isVisible]);

  return (
    <Container>
      <PopoverContent
        isOpen={isVisible}
        style={getPositionStyles(align, vertical)}
        trigger={(props) => (
          <Trigger
            ref={triggerRef}
            onMouseEnter={handleTriggerMouseEnter}
            onMouseLeave={handleTriggerMouseLeave}
            {...props}
          >
            {children}
          </Trigger>
        )}
      >
        <div
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            // Invisible padding extends toward the trigger to bridge the gap,
            // so the mouse stays within the hover zone while traveling between
            // the trigger and the tooltip.
            padding: vertical === "above" ? "0 8px 8px" : "8px 8px 0",
            margin: vertical === "above" ? "0 -8px -8px" : "-8px -8px 0",
            cursor: "default",
          }}
        >
          <UrlText variant="text-small-md">{truncateMiddle(url, 30)}</UrlText>
          <ButtonGroup onMouseLeave={() => setHoveredButton(null)}>
            <ButtonHighlight
              style={{
                opacity: hoveredButton !== null ? 1 : 0,
                transform: `translateX(${(hoveredButton ?? 0) * 20}px)`,
              }}
            />
            <ActionButton
              onMouseDown={handleActionMouseDown}
              onPress={handleCopy}
              onMouseEnter={() => setHoveredButton(0)}
            >
              <AnimatedCopyIcon copied={copied} />
            </ActionButton>
            <ActionButton
              onMouseDown={handleActionMouseDown}
              onPress={handleOpen}
              onMouseEnter={() => setHoveredButton(1)}
            >
              <Icon name="arrow-up-right" size={12} />
            </ActionButton>
          </ButtonGroup>
        </div>
      </PopoverContent>
    </Container>
  );
}
